import { LearnMoreLink, Page } from '@ui/themed';
import { t } from 'i18next';
import { featureRestricted, features, getActiveBillingPlanKey } from '../../../constants/features';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { ShippingMethodFormContext, useShippingFormLogic } from '../builder-context';
import { ShippingMethodBuilder } from '../components/shipping-method-builder';
import { useCreateShippingMethod } from '../hooks/use-create-shipping-method';
import type { ShippingMethodBuilderValidated } from '../schemas';
import { transformShippingMethodToApi } from '../transformers';

export const ShippingMethodCreatePage = withPageProviders(ShippingMethodCreatePageContent);

function ShippingMethodCreatePageContent() {
  const { navigationHandler, notificationHandler } = useHandlers();
  const createMethod = useCreateShippingMethod();
  const env = useEnv();

  const shippingForm = useShippingFormLogic({
    onSubmit: handleSubmit,
    onError: () => {
      notificationHandler.error();
    },
  });

  if (featureRestricted(features.SHIPPING, getActiveBillingPlanKey(env.billing.plan_key))) {
    navigationHandler.navigate('/configuration/shipping');
    return null;
  }

  async function handleSubmit(data: ShippingMethodBuilderValidated) {
    const payload = transformShippingMethodToApi(data);
    await createMethod.mutateAsync(payload, {
      onError: () => {
        notificationHandler.error();
      },
      onSuccess: () => {
        notificationHandler.success(
          t(
            'features:shipping_methods.components.shipping_method_builder.notifications.success.create_successful',
            { name: data.name },
          ),
        );

        navigationHandler.navigate('/configuration/shipping');
      },
    });
  }

  return (
    <Page
      narrow
      title={t('features:shipping_methods.components.shipping_method_builder.header.title.create')}
      backUrl="/configuration/shipping"
      description={
        <>
          {t('features:shipping_methods.components.shipping_method_builder.header.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/shipping#XsUtC" />
        </>
      }
    >
      <ShippingMethodFormContext.Provider value={shippingForm}>
        <ShippingMethodBuilder
          onCancel={() => navigationHandler.navigate('/configuration/shipping')}
        />
      </ShippingMethodFormContext.Provider>
    </Page>
  );
}
